import React from 'react';
import Container from './shared/Container';
import useScrollFade from '../hooks/useScrollFade';

const OperationalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const DecisionIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FinancialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CustomerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const benefits = [
  {
    id: "operational",
    Icon: OperationalIcon,
    title: "Operational Excellence",
    description: "Reduce processing time by up to 60% while increasing team capacity by 40%. Automation handles routine tasks, allowing your team to focus on complex cases."
  },
  {
    id: "decision",
    Icon: DecisionIcon,
    title: "Decision Confidence",
    description: "Reduce errors and inconsistencies across your team. Every case is analyzed against the same criteria, eliminating variability in complex decisions."
  },
  {
    id: "financial",
    Icon: FinancialIcon,
    title: "Financial Impact",
    description: "Achieve significant cost savings through efficient operations, reduced leakage, and improved fraud detection. Our customers typically see a 4–8× return on investment within the first year."
  },
  {
    id: "customer",
    Icon: CustomerIcon,
    title: "Built-in Accountability",
    description: "Every extraction and decision produces a traceable record. Auditors, regulators, and your own QA team can verify exactly what ran, when, and why."
  }
];

const BenefitsSection = () => {
  const [ref, visible] = useScrollFade();
  return (
    <section id="benefits" className="py-16 sm:py-24 bg-surface-secondary">
      <Container>
        <div ref={ref} className={`lg:grid lg:grid-cols-12 lg:gap-16 items-start scroll-fade${visible ? ' visible' : ''}`}>
          <div className="lg:col-span-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              The Vermillion Advantage
            </h2>
            <p className="mt-4 text-lg text-gray-500 leading-relaxed">
              Purpose-built for operations teams that need to move faster without sacrificing accuracy.
            </p>
          </div>

          <div className="mt-10 lg:mt-0 lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            {benefits.map(({ id, Icon, title, description }) => (
              <div key={id}>
                <div className="text-vermillion-600 mb-3">
                  <Icon />
                </div>
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <p className="mt-1 text-sm text-gray-500 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default BenefitsSection;
